import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isAdminContext } from 'src/api';
import { useAccount } from 'src/core/hooks/account';
import { LanguageKeys, LANGUAGE_KEYS, useAppMeta } from 'src/core/provider/AppMeta';

export const shouldApplyStoredLanguage = (
    currentLanguage: string,
    storedLanguageIso2: string | undefined,
    alreadyAppliedForCustomerId: number | null,
    customerId: number | undefined
): boolean => {
    if (customerId === undefined || customerId === alreadyAppliedForCustomerId) return false;
    if (!storedLanguageIso2) return false;

    const currentLanguagePart = (currentLanguage ?? '').split('-')[0].toLowerCase();
    return storedLanguageIso2.toLowerCase() !== currentLanguagePart;
};

/**
 * Applies the logged-in customer's stored language (customerData.languageIso2) once per
 * customer id, keeping the current region. Anonymous visitors and the admin context
 * (where the customerData slice holds an admin-viewed customer) are unaffected.
 */
export const useStoredLanguageAutoApply = (): void => {
    const { isLoggedIn } = useAccount();
    const { selectedLocale, setLanguage } = useAppMeta();
    const customerId = useSelector((state: any): number | undefined => state.customerData?.content?.id);
    const storedLanguageIso2 = useSelector(
        (state: any): string | undefined => state.customerData?.content?.languageIso2
    );
    const appliedForCustomerId = useRef<number | null>(null);

    useEffect(() => {
        if (!isLoggedIn || isAdminContext() || customerId === undefined) return;

        const apply = shouldApplyStoredLanguage(
            selectedLocale,
            storedLanguageIso2,
            appliedForCustomerId.current,
            customerId
        );
        // Mark the customer as handled even without a change, so a later refetch of
        // customerData never overrides a manual switch via CountryLangSelector.
        appliedForCustomerId.current = customerId;
        if (!apply) return;

        const storedLanguage = storedLanguageIso2?.toLowerCase() as LanguageKeys;
        if (!LANGUAGE_KEYS.includes(storedLanguage)) return;

        setLanguage(storedLanguage, false);
    }, [isLoggedIn, customerId, storedLanguageIso2, selectedLocale, setLanguage]);
};
